import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { computeDropdownPosition } from './dropdownPosition';
import type { ReportStatus } from '../../lib/reports';
import {
  ArrowUturnLeftIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  PrinterIcon,
  ShareIcon,
  TrashIcon,
} from '../icons';

/** 메뉴 항목 아이콘 공통 크기 — 라벨(text-sm) 옆 정렬 기준. */
const MENU_ICON_CLASS = 'h-4 w-4 flex-shrink-0';

/** 메뉴 최소 폭 — 아래 `min-w-[180px]`와 같은 값. 실측 전 위치 계산의 폴백으로만 쓴다. */
const MENU_MIN_WIDTH = 180;

interface MenuItem {
  id: string;
  label: string;
  /** SVG 아이콘 노드. 글꼴 글리프/이모지 금지(크기 클래스가 먹지 않는다). */
  icon?: ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  divider?: boolean;
}

interface ReportDropdownMenuProps {
  /** 보고서 상태 */
  // 손나열 union은 CANCELLED 누락 전례(lib/reports.ts 2026-09-02 리뷰) — 파생 정본을 쓴다.
  status: ReportStatus;
  /** 삭제 가능 여부 */
  canDelete?: boolean;
  /** 삭제 불가 사유 */
  deleteDisabledReason?: string;
  /** 상세 보기 */
  onView?: () => void;
  /** 인쇄 (PDF 다운로드) */
  onPrint?: () => void;
  /** 공유 */
  onShare?: () => void;
  /** 삭제 */
  onDelete?: () => void;
  /** 삭제 승인 대기 중 여부 */
  hasPendingDeleteRequest?: boolean;
  /** 삭제 요청 취소 */
  onCancelDeleteRequest?: () => void;
}

/**
 * 보고서 목록용 드롭다운 메뉴
 */
export function ReportDropdownMenu({
  status,
  canDelete = false,
  deleteDisabledReason,
  onView,
  onPrint,
  onShare,
  onDelete,
  hasPendingDeleteRequest = false,
  onCancelDeleteRequest,
}: ReportDropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  // 측정 전에는 null — 확정 전 좌표로 한 프레임 깜빡이지 않게 숨긴 채 렌더한다.
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // 메뉴 위치 계산 — 아래 공간이 부족하면 위로 뒤집는다(목록 하단 항목에서 잘림 방지).
  // useLayoutEffect: 메뉴가 커밋된 뒤 실제 크기를 재고 페인트 전에 위치를 확정한다.
  // (항목 수·삭제 사유 유무로 높이가 달라져 상수로는 flip 여부를 판단할 수 없다.)
  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return;
    }
    if (!buttonRef.current || !menuRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const { top, left } = computeDropdownPosition({
      trigger: { top: rect.top, bottom: rect.bottom, right: rect.right },
      menuWidth: menuRef.current.offsetWidth || MENU_MIN_WIDTH,
      menuHeight: menuRef.current.offsetHeight,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });
    setMenuPosition({ top, left });
  }, [isOpen]);

  // 외부 클릭 감지
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // ESC 키로 닫기
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // 스크롤 시 닫기
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const handleItemClick = (item: MenuItem) => {
    if (item.disabled) return;
    item.onClick();
    setIsOpen(false);
  };

  // 메뉴 항목 구성
  const items: MenuItem[] = [];

  // 상세 보기
  if (onView) {
    items.push({
      id: 'view',
      label: '상세 보기',
      icon: <EyeIcon className={MENU_ICON_CLASS} />,
      onClick: onView,
    });
  }

  // 인쇄 (PDF) - 승인된 보고서만 (항상 활성화, 클릭 시 상태에 따라 처리)
  if (onPrint && status === 'APPROVED') {
    items.push({
      id: 'print',
      label: 'PDF 다운로드',
      icon: <PrinterIcon className={MENU_ICON_CLASS} />,
      onClick: onPrint,
    });
  }

  // 공유 - 승인된 보고서만
  if (onShare && status === 'APPROVED') {
    items.push({
      id: 'share',
      label: '공유',
      icon: <ShareIcon className={MENU_ICON_CLASS} />,
      onClick: onShare,
    });
  }

  // 삭제 요청 취소 (PENDING 삭제 요청이 있는 경우)
  if (hasPendingDeleteRequest && onCancelDeleteRequest) {
    items.push({
      id: 'cancel-delete-request',
      label: '삭제 요청 취소',
      icon: <ArrowUturnLeftIcon className={MENU_ICON_CLASS} />,
      onClick: onCancelDeleteRequest,
      danger: true,
      divider: true,
    });
  } else if (onDelete) {
    // 삭제 - 모든 상태에서 표시
    items.push({
      id: 'delete',
      label: '삭제',
      icon: <TrashIcon className={MENU_ICON_CLASS} />,
      onClick: onDelete,
      danger: canDelete,
      disabled: !canDelete,
      disabledReason: deleteDisabledReason,
      divider: true,
    });
  }

  // 메뉴 항목이 없으면 렌더링하지 않음
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="relative inline-block">
      {/* 트리거 버튼 */}
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation(); // 테이블 행 클릭 이벤트 방지
          setIsOpen(!isOpen);
        }}
        className="p-2 text-ink-400 hover:text-ink-600 rounded hover:bg-ink-100 transition-colors"
      >
        <EllipsisHorizontalIcon className="h-5 w-5" />
      </button>

      {/* 드롭다운 메뉴 (Portal로 body에 렌더링) */}
      {isOpen && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPosition?.top ?? 0,
            left: menuPosition?.left ?? 0,
            // 위치 확정 전(첫 커밋, 크기 측정 전)에는 숨긴다. 크기는 그대로 재지므로
            // display:none이 아니라 visibility여야 한다.
            visibility: menuPosition ? 'visible' : 'hidden',
          }}
          className="z-[9999] py-1 bg-white rounded-card shadow-lg border min-w-[180px] animate-fade-in"
        >
          {items.map((item, index) => (
            <div key={item.id}>
              {item.divider && index > 0 && (
                <div className="h-px bg-ink-100 my-1" />
              )}
              <div className="relative group">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleItemClick(item);
                  }}
                  disabled={item.disabled}
                  className={`
                    w-full px-4 py-2 text-sm text-left flex items-center gap-2
                    transition-colors
                    ${item.disabled
                      ? 'text-ink-300 cursor-not-allowed'
                      : item.danger
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-ink-700 hover:bg-ink-50'
                    }
                  `}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
                {item.disabled && item.disabledReason && (
                  <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 px-2 py-1 text-xs text-white bg-ink-800 rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[10000] max-w-[250px]">
                    {item.disabledReason}
                    <div className="absolute left-full top-1/2 -translate-y-1/2 w-0 h-0 border-y-4 border-y-transparent border-l-4 border-l-ink-800" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default ReportDropdownMenu;
